import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [debugToken, setDebugToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      
      // For testing - show debug token
      if (response.data.debug_token) {
        setDebugToken(response.data.debug_token);
      }
      
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[#00FF94]/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#00FF94]" />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">Forgot Password?</CardTitle>
          <CardDescription className="text-gray-500">
            {submitted 
              ? "We've sent you a password reset link" 
              : "Enter your email and we'll send you a reset link"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 bg-[#171717] border-[#262626] text-white"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn-primary"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-gray-500 hover:text-[#00FF94] inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="text-center py-4">
                <CheckCircle2 className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
                <p className="text-gray-400">
                  If an account exists with <span className="text-white">{email}</span>, 
                  you'll receive an email with instructions to reset your password.
                </p>
              </div>

              {/* Debug token for testing - remove in production */}
              {debugToken && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-400 text-sm font-semibold mb-2">🧪 Test Mode - Reset Link:</p>
                  <Link 
                    to={`/reset-password?token=${debugToken}`}
                    className="text-[#00FF94] text-sm break-all hover:underline"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}

              <div className="space-y-2">
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                    setDebugToken(null);
                  }}
                  variant="outline"
                  className="w-full border-[#262626] text-gray-400 hover:bg-[#171717]"
                >
                  Try Another Email
                </Button>
                
                <Link to="/login">
                  <Button className="w-full btn-primary">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
